/**
 * Host-side container that renders the shared `SpaceLandingPage` inside the
 * conversation-assistant sidebar (and any other non-Townsquare host) when
 * `activeMenu === 'space-landing'`. Mirrors the Townsquare fullscreen wiring
 * in `townsquare/views/chat/src/components/ChatView/ChatView.tsx` so behaviour
 * is identical across surfaces — the only difference is that this container
 * also renders a `ChatHeader` with a back button so the user can return to
 * the chat surface from inside the sidebar.
 *
 * Primary rendering is gated behind `rovo_chat_spaces_experiment` by the caller
 * in `main.tsx`; this container also guards its data fetches defensively.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import { RovoChatPromptInput } from '@atlassian/conversation-assistant-chat-prompt-input/RovoChatPromptInput';
import type { SelectedAgentChangePayload } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import type { ChatPromptInputProps } from '@atlassian/conversation-assistant-chat-prompt-input/ui/chat-prompt-input/types';
import { logSentryConsoleError } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	useSelectedSpaceSelector,
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useAddSpaceSource } from '@atlassian/rovo-spaces/services/useAddSpaceSource';
import { useDeleteSpaceSource } from '@atlassian/rovo-spaces/services/useDeleteSpaceSource';
import { useGetSpace } from '@atlassian/rovo-spaces/services/useGetSpace';
import { useGetSpaceConversations } from '@atlassian/rovo-spaces/services/useGetSpaceConversations';
import { useUpdateSpace } from '@atlassian/rovo-spaces/services/useUpdateSpace';
import type { RovoNavigationConversation } from '@atlassian/rovo-spaces/types/rovo-navigation-conversation';
import { SpaceLandingPage } from '@atlassian/rovo-spaces/ui/SpaceLandingPage';
import type {
	SpaceChatSubmitParams,
	SpaceDetailsSaveParams,
	SpaceSource,
	SpaceStarter,
} from '@atlassian/rovo-spaces/ui/SpaceLandingPage/types';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useChatStore } from '../../controllers/chat-store/useChatStore';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import messages from '../chat-input-refresh/messages';
import { useInputHatSelectedAgent } from '../chat-input-refresh/useInputHatSelectedAgent';

import type { LocalSpaceConversation } from './mergeSpaceConversations';
import { mergeSpaceConversations } from './mergeSpaceConversations';

const styles = cssMap({
	wrapper: {
		inlineSize: '100%',
		blockSize: '100%',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		minHeight: '0px',
	},
	// Mini-modal (non-fullscreen): the mount point is a flex column bounded by
	// `maxHeight`, not a definite `height`, so `blockSize: 100%` has no base to
	// resolve against. Grow via flex to give a definite height for descendants.
	wrapperModal: {
		blockSize: 'auto',
		flexGrow: 1,
		minHeight: '0px',
	},
	headerWrapper: {
		flexShrink: 0,
	},
	scrollableContent: {
		flexGrow: 1,
		overflowY: 'auto',
		overflowX: 'hidden',
		minHeight: '0px',
	},
	// Mini-modal (non-fullscreen): `SpaceLandingPage` owns its own scroll region
	// and pins its chat input. Hide overflow (so this container isn't a second
	// scroller) and be a flex column (so the child's `height: 100%` resolves and
	// its `scrollArea` can shrink/scroll, keeping the input pinned). Fullscreen
	// keeps `scrollableContent`'s `overflowY: 'auto'`.
	scrollableContentModal: {
		overflowY: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		minHeight: '0px',
	},
	spaceContextInputWrapper: {
		marginInline: token('space.200'),
	},
});

export interface SpaceLandingPageContainerProps {
	/** Header rendered above the landing page when not in fullscreen mode. */
	chatRefreshHeader?: React.ReactNode;
}

type SpaceLandingPageContainerInnerProps = SpaceLandingPageContainerProps & {
	/** `selectedSpace` read from the ui-store, threaded in by the gate wrappers below. */
	selectedSpace: ReturnType<typeof useSelectedSpaceSelector>[0]['selectedSpace'];
};

const SpaceLandingPageContainerComponent = ({
	chatRefreshHeader,
	selectedSpace,
}: SpaceLandingPageContainerInnerProps): React.JSX.Element | null => {
	const { fullScreen } = useUIConfig();
	const { siteId, experienceId, productHeader, emojiProvider, orgId } = useGlobalConfig();
	const { clearSelectedSpace, setActiveMenu } = useUIStoreActions();
	const [{ conversations: localConversations }] = useChatStore();
	const { setSelectedConversation } = useChatStoreActions();
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	const spaceId = selectedSpace?.id ?? '';

	const [selectedAgentPayload, setSelectedAgentPayload] = useState<
		SelectedAgentChangePayload | undefined
	>(undefined);
	const activeSelectedAgentId = selectedAgentPayload?.id;
	const selectedAgentForInputHat = useInputHatSelectedAgent({
		conversationId: spaceId,
		activeSelectedAgentId,
		selectedAgentIdentityAccountId: selectedAgentPayload?.identityAccountId,
	});
	const isSpacesEnabled = FeatureGates.getExperimentValue(
		'rovo_chat_spaces_experiment',
		'isEnabled',
		false,
	);
	const enabled = isSpacesEnabled && !!selectedSpace?.id;

	const {
		space: spaceData,
		isLoading: isSpaceLoading,
		refetch: refetchSpace,
	} = useGetSpace({
		spaceId,
		cloudId: siteId,
		product: productHeader,
		experienceId,
		enabled,
	});
	const displayedSpaceName = spaceData?.name ?? selectedSpace?.name ?? '';
	const displayedSpaceEmoji = spaceData?.emoji ?? selectedSpace?.emoji ?? '';
	const displayedSpaceDescription = spaceData?.description ?? undefined;

	const { conversationPage, isLoading: isChatsLoading } = useGetSpaceConversations({
		spaceId,
		cloudId: siteId,
		product: productHeader ?? 'rovo',
		experienceId: experienceId ?? 'ai-mate',
		enabled,
	});

	const { addSource } = useAddSpaceSource({
		spaceId,
		cloudId: siteId,
		product: productHeader,
		experienceId,
	});

	const { deleteSource } = useDeleteSpaceSource({
		spaceId,
		cloudId: siteId,
		product: productHeader,
		experienceId,
	});

	const { mutate: updateSpace } = useUpdateSpace({
		spaceId,
		cloudId: siteId,
		product: productHeader ?? 'rovo',
		experienceId: experienceId ?? 'ai-mate',
	});

	const [optimisticSources, setOptimisticSources] = useState<SpaceSource[]>([]);
	const [optimisticDeletedSourceIds, setOptimisticDeletedSourceIds] = useState<Set<string>>(
		new Set(),
	);

	useEffect(() => {
		setOptimisticSources([]);
		setOptimisticDeletedSourceIds(new Set());
		setSelectedAgentPayload(undefined);
	}, [spaceId]);

	useEffect(() => {
		if (!spaceData?.sources) {
			return;
		}
		const serverUrls = new Set(spaceData.sources.map((s) => s.url));
		setOptimisticSources((prev) => prev.filter((s) => !serverUrls.has(s.url)));
	}, [spaceData]);

	const starters: SpaceStarter[] = useMemo(
		() => spaceData?.starters?.map((s) => ({ id: s.id, text: s.text })) ?? [],
		[spaceData],
	);

	const sources: SpaceSource[] = useMemo(
		() => [
			...(spaceData?.sources
				?.filter((s) => !optimisticDeletedSourceIds.has(s.id))
				.map(
					(s): SpaceSource => ({
						id: s.id,
						title: s.name ?? s.url,
						url: s.url,
						sourceType: s.sourceType as SpaceSource['sourceType'],
					}),
				) ?? []),
			...optimisticSources.filter(
				(s) => !optimisticDeletedSourceIds.has(s.id) && !optimisticDeletedSourceIds.has(s.url),
			),
		],
		[spaceData, optimisticSources, optimisticDeletedSourceIds],
	);
	const areSourcesKnown = (!isSpaceLoading && Boolean(spaceData)) || optimisticSources.length > 0;

	const chats = useMemo(
		() =>
			mergeSpaceConversations({
				apiConversations: conversationPage?.conversations ?? [],
				localConversations: Object.values(localConversations) as LocalSpaceConversation[],
				spaceId,
			}),
		[conversationPage, localConversations, spaceId],
	);

	const handleStarterClick = useCallback(
		(starter: SpaceStarter) => {
			if (!selectedSpace) {
				return;
			}
			publish({
				type: 'chat-new',
				source: 'space-landing-starter',
				data: {
					prompt: starter.text,
					dialogues: [],
					resetActiveMenu: true,
					spaceId: selectedSpace.id,
					isPromptPlaceholder: true,
					placeholderType: 'skill',
				},
			});
		},
		[publish, selectedSpace],
	);

	const handleChatSubmit = useCallback(
		(params: SpaceChatSubmitParams) => {
			if (!selectedSpace) {
				return;
			}
			publish({
				type: 'chat-new',
				source: 'space-landing-chat-input',
				data: {
					prompt: params.prompt,
					dialogues: [],
					resetActiveMenu: true,
					spaceId: selectedSpace.id,
					...(params.agentId ? { agentId: params.agentId } : {}),
					mode: {
						deepResearchEnabled: params.deepResearchEnabled,
						thinkDeeperEnabled: params.thinkDeeperEnabled,
						fastModeEnabled: params.fastModeEnabled,
						webSearchEnabled: params.webSearchEnabled,
						sourceMode: params.sourceMode,
					},
				},
			});
		},
		[publish, selectedSpace],
	);

	const handleChatClick = useCallback(
		(chat: RovoNavigationConversation) => {
			clearSelectedSpace();
			setSelectedConversation({
				conversationId: chat.id,
				conversationMetadata: chat,
			});
			setActiveMenu('none');
		},
		[clearSelectedSpace, setActiveMenu, setSelectedConversation],
	);

	const handleSpaceDetailsSave = useCallback(
		async ({
			title: nextTitle,
			description: nextDescription,
			emoji: nextEmoji,
		}: SpaceDetailsSaveParams) => {
			let updateError: unknown;

			await updateSpace(
				{
					name: nextTitle,
					description: nextDescription,
					emoji: (nextEmoji ?? displayedSpaceEmoji) || null,
				},
				{
					onError: (error) => {
						updateError = error;
					},
				},
			);

			if (updateError) {
				throw updateError instanceof Error ? updateError : new Error(String(updateError));
			}

			try {
				await refetchSpace();
			} catch {
				// The mutation patches the shared space store; a refetch miss should not
				// make a successful inline edit look failed.
			}
		},
		[displayedSpaceEmoji, refetchSpace, updateSpace],
	);

	// Builds the chat prompt input that SpaceLandingPage embeds below the page
	// content. Hosted here (rather than inside SpaceLandingPage itself) to keep
	// `rovo-spaces` independent of `conversation-assistant-chat-prompt-input`
	// and avoid the package dependency cycle.
	const assistanceServiceParams = useMemo(
		() => ({
			product: productHeader ?? 'rovo',
			experienceId: 'rovo-spaces',
			touchpointSource: 'space-landing-page',
			siteId: siteId ?? '',
		}),
		[productHeader, siteId],
	);

	const renderChatInput = useCallback<
		NonNullable<React.ComponentProps<typeof SpaceLandingPage>['renderChatInput']>
	>(
		({ sources: chatInputSources, onSubmit, zeroSourcesBannerAction }) => {
			const handlePromptSubmit: NonNullable<ChatPromptInputProps['onSubmit']> = (prompt, mode) =>
				onSubmit({
					prompt,
					deepResearchEnabled: mode.deepResearchEnabled,
					thinkDeeperEnabled: mode.thinkDeeperEnabled,
					fastModeEnabled: mode.fastModeEnabled,
					webSearchEnabled: mode.webSearchEnabled,
					sourceMode: mode.sourceMode,
					agentId: mode.inputSelectedAgentId,
				});
			const isMentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');
			return (
				<Box xcss={styles.spaceContextInputWrapper}>
					<RovoChatPromptInput
						ref={null}
						isConversationAgentic={false}
						assistanceServiceParams={assistanceServiceParams}
						arrowKeyPluginConfigMessages={[]}
						productKey={productHeader ?? 'rovo'}
						siteId={siteId ?? ''}
						orgId={orgId}
						isAgentTyping={false}
						promptDraftAndModeKey={spaceId}
						isSpaceScopedConversation
						spaceSourceCount={areSourcesKnown ? chatInputSources.length : undefined}
						spaceSources={chatInputSources.map((s) => ({
							id: s.id,
							ari: s.url,
							url: s.url,
							sourceType: s.sourceType ?? '',
							name: s.title,
						}))}
						zeroSourcesAction={zeroSourcesBannerAction}
						clearModeOnSubmit
						onSubmit={handlePromptSubmit}
						onAbort={() => {}}
						adaptiveHeight
						enableAgentMentions={isMentionAgentsExperimentEnabled}
						onSelectedAgentChange={setSelectedAgentPayload}
						selectedAgentForInputHat={selectedAgentForInputHat}
						disabledAgentTooltip={
							isMentionAgentsExperimentEnabled
								? formatMessage(messages.chatInputDisabledAgentTooltip)
								: undefined
						}
						disableWrapperMarginStyles
						// Opt the Rovo Spaces landing page chat input into apps discovery UI
						// (limited unconnected apps, "Discover more apps" footer).
						enableAppsDiscoveryUi
					/>
				</Box>
			);
		},
		[
			assistanceServiceParams,
			areSourcesKnown,
			productHeader,
			siteId,
			orgId,
			formatMessage,
			selectedAgentForInputHat,
			spaceId,
			setSelectedAgentPayload,
		],
	);

	const handleDeleteSource = useCallback(
		async (source: SpaceSource) => {
			setOptimisticDeletedSourceIds((prev) => new Set([...prev, source.id]));
			try {
				await deleteSource(source.id);
				setOptimisticSources((prev) =>
					prev.filter((s) => s.id !== source.id && s.url !== source.url),
				);
				try {
					await refetchSpace();
				} catch {
					// Refetch failure is non-fatal — the source is already deleted server-side.
				}
				setOptimisticDeletedSourceIds((prev) => {
					const next = new Set(prev);
					next.delete(source.id);
					return next;
				});
			} catch (error) {
				setOptimisticDeletedSourceIds((prev) => {
					const next = new Set(prev);
					next.delete(source.id);
					return next;
				});
				logSentryConsoleError('Failed to delete source from space', error);
			}
		},
		[deleteSource, refetchSpace],
	);

	const handleSourceAdded = useCallback(
		async (source: SpaceSource) => {
			setOptimisticSources((prev) => [...prev, source]);
			try {
				await addSource({
					url: source.url,
					name: source.title,
					sourceType: source.sourceType,
				});
			} catch (error) {
				setOptimisticSources((prev) => prev.filter((s) => s.id !== source.id));
				logSentryConsoleError('Failed to add source to space', error);
				return;
			}
			try {
				await refetchSpace();
			} catch {
				// Refetch failure is non-fatal — the source is already added server-side
				// and the optimistic copy keeps the UI in sync until the next refetch.
			}
		},
		[addSource, refetchSpace],
	);

	if (!isSpacesEnabled || !selectedSpace) {
		return null;
	}

	return (
		<Stack xcss={cx(styles.wrapper, !fullScreen && styles.wrapperModal)}>
			{!fullScreen && chatRefreshHeader && (
				<Box xcss={styles.headerWrapper}>{chatRefreshHeader}</Box>
			)}
			<Box xcss={cx(styles.scrollableContent, !fullScreen && styles.scrollableContentModal)}>
				<SpaceLandingPage
					fullScreen={fullScreen}
					spaceId={selectedSpace.id}
					cloudId={siteId}
					emojiProvider={emojiProvider}
					emoji={displayedSpaceEmoji}
					title={displayedSpaceName}
					entryPoint={selectedSpace.entryPoint}
					description={displayedSpaceDescription}
					isDescriptionLoading={isSpaceLoading && !displayedSpaceDescription}
					onSpaceDetailsSave={handleSpaceDetailsSave}
					starters={starters}
					sources={sources}
					chats={chats}
					isChatsLoading={isChatsLoading}
					onStarterClick={handleStarterClick}
					onChatClick={handleChatClick}
					onChatSubmit={handleChatSubmit}
					renderChatInput={renderChatInput}
					onDeleteSource={handleDeleteSource}
					onSourceAdded={handleSourceAdded}
				/>
			</Box>
		</Stack>
	);
};

// rovo_ttai_segment_render_stabilization: absorb parent render waves when props are stable.
const MemoizedSpaceLandingPageContainer = React.memo(
	SpaceLandingPageContainerComponent,
	shallowEqualObjects,
);

// rovo_ttai_segment_render_stabilization ON: subscribe to `selectedSpace` via the
// narrow selector so the component only re-renders when `selectedSpace` changes,
// rather than on every UI-store state change.
const SpaceLandingPageContainerWithSelector = (
	props: SpaceLandingPageContainerProps,
): React.JSX.Element | null => {
	di(useSelectedSpaceSelector);
	const [{ selectedSpace }] = useSelectedSpaceSelector();
	return <MemoizedSpaceLandingPageContainer {...props} selectedSpace={selectedSpace} />;
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI
// store, matching the prior behaviour where the component re-rendered on any
// UI-store state change.
const SpaceLandingPageContainerWithBroadStore = (
	props: SpaceLandingPageContainerProps,
): React.JSX.Element | null => {
	di(useUIStore);
	const { selectedSpace } = useUIStore();
	return <SpaceLandingPageContainerComponent {...props} selectedSpace={selectedSpace} />;
};

export const SpaceLandingPageContainer = (
	props: SpaceLandingPageContainerProps,
): React.JSX.Element | null => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<SpaceLandingPageContainerWithSelector {...props} />
	) : (
		<SpaceLandingPageContainerWithBroadStore {...props} />
	);
};
